import { currentFirm } from '@/lib/current-firm';
import { companyName, type Branding } from '@/lib/branding';
import { esc, fmtDate, fmtMoney } from '@/lib/dom';
import { photoPath } from '@/lib/branding';
import { brandFontStack, brandColor, brandFontHref } from '@/lib/brand-type';
import { logoUrl } from '@/lib/data/branding';
import { ROOM_STATUS_LABEL, SPEC_STATUS_LABEL } from '@/lib/labels';
import { punchVendorName } from '@/lib/punch';
import { QUESTIONNAIRE_FIELDS, readResponses } from '@/lib/data/questionnaires';
import type {
  ProjectRow, RoomRow, SpecRow, DesignerContactRow, PunchItemRow,
  PaletteColorRow, QuestionnaireRow, 
} from '@/types/database';

const PRINT_HEAD = (title: string): string => `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>${brandFontLink()}<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1a1714;padding:32px;line-height:1.45}
  h1{font-family:Georgia,'Cormorant Garamond',serif;font-size:26px;font-weight:400;margin-bottom:4px}
  h2{font-family:Georgia,'Cormorant Garamond',serif;font-size:18px;font-weight:400;margin:24px 0 10px}
  table{width:100%;border-collapse:collapse;margin-bottom:18px}
  th{background:#f5f0e8;padding:8px 10px;text-align:left;font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#8a837a;border-bottom:1px solid #e2d9cc}
  td{padding:8px 10px;border-bottom:1px solid #e2d9cc;vertical-align:top}
  .header{margin-bottom:28px;padding-bottom:14px;border-bottom:2px solid #B89A6A}
  .brand-row{display:flex;align-items:center;gap:10px;margin-bottom:6px}
  .brand-photo{width:34px;height:34px;border-radius:50%;object-fit:cover;flex-shrink:0}
  /* font-family and color are set inline per firm (0045); these are the
     fallbacks for a firm that has chosen neither. */
  .brand{font-family:Georgia,serif;font-size:11px;letter-spacing:.2em;color:#B89A6A;text-transform:uppercase}
  .meta{font-size:11px;color:#8a837a;margin-top:6px}
  .page-break{page-break-before:always}
  .swatch-row{display:flex;gap:14px;flex-wrap:wrap;margin-bottom:18px}
  .swatch-row .sw{width:120px}
  .swatch-row .sw .chip{width:120px;height:60px;border:1px solid #e2d9cc;border-radius:4px}
  .swatch-row .sw .name{font-weight:600;margin-top:6px}
  .swatch-row .sw .meta{font-size:10px}
  .q-block{margin-bottom:14px}
  .q-block .q{font-family:Georgia,serif;font-style:italic;color:#8a837a;font-size:11px;letter-spacing:.05em;text-transform:uppercase;margin-bottom:4px}
  .q-block .a{font-size:13px;white-space:pre-wrap;border-left:2px solid #B89A6A;padding-left:10px;color:#1a1714}
  .q-block .a.empty{color:#bbb;font-style:italic;border-color:#eee}
  @media print{@page{margin:18mm}}
</style></head><body>`;

const PRINT_FOOT = `</body></html>`;

/** Set once per export so `header()` doesn't need threading through every builder. */
let exportBrand: Branding | null = null;
let exportBrandLoaded = false;
let exportBrandInFlight: Promise<void> | null = null;

/** Branding for printed output. Call before building; safe to omit. */
export function setExportBranding(b: Branding | null): void {
  exportBrand = b;
  exportBrandLoaded = true;
}

/** Whether the firm's branding has been fetched this session. */
export function hasExportBranding(): boolean {
  return exportBrandLoaded;
}

/**
 * The firm's branding as already fetched this session, or null.
 *
 * For anything else that must NAME the firm without spending a request — a
 * message to a client signs off with the firm's name, and `primeExportBranding`
 * has usually already answered by the time anyone taps Send. Null is fine:
 * `companyName(null, firmName)` falls back to the firm's own name.
 */
export function exportBranding(): Branding | null {
  return exportBrand;
}

/**
 * Fetch the firm's branding for printed output — ONCE per session.
 *
 * The project page fired this on every paint, so every section click cost a
 * request for a value only Settings can change (2026-09-22). Guarded on the
 * IN-FLIGHT promise as well as the loaded flag: three quick clicks all happen
 * before the first answer comes back, and a flag set on arrival would let all
 * three through.
 *
 * Fire-and-forget by design. An export triggered before it resolves falls
 * back to the firm's own name, which is what `companyName()` does anyway.
 */
export function primeExportBranding(load: () => Promise<Branding | null>): void {
  if (exportBrandLoaded || exportBrandInFlight) return;
  exportBrandInFlight = load()
    .then((b) => { setExportBranding(b); })
    .catch(() => { /* the built-in name is a fine fallback */ })
    .finally(() => { exportBrandInFlight = null; });
}

/** Forget it, so the next page that needs it re-reads. Settings calls this on save. */
export function clearExportBranding(): void {
  exportBrand = null;
  exportBrandLoaded = false;
  exportBrandInFlight = null;
}

function header(p: ProjectRow, title: string): string {
  // The designer's face on the page a client is handed (0045). A print window
  // is a separate document, so the font has to come with it — `brandFontHref`
  // is added to the head in PRINT_HEAD below, not injected into this one.
  const photo = photoPath(exportBrand);
  const nameStyle = `font-family:${brandFontStack(exportBrand?.brand_font)};`
    + `color:${brandColor(exportBrand?.brand_color)}`;
  return `<div class="header">
    <div class="brand-row">
      ${photo ? `<img class="brand-photo" src="${esc(logoUrl(photo))}" alt="" />` : ''}
      <div class="brand" style="${esc(nameStyle)}">${esc(companyName(exportBrand, currentFirm()?.name))}</div>
    </div>
    <h1>${esc(title)}</h1>
    <div class="meta">${esc(p.name)}${p.client_name ? ` · ${esc(p.client_name)}` : ''} · Generated ${esc(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))}</div>
  </div>`;
}

/** The <link> a printed page needs for the firm's typeface, or ''. */
function brandFontLink(): string {
  const href = brandFontHref(exportBrand?.brand_font);
  return href ? `<link rel="stylesheet" href="${esc(href)}" />` : '';
}

function openPrint(html: string): void {
  // Note: cannot pass 'noopener' here — Chrome then returns null / a detached
  // window and document.write becomes a no-op (blank tab). The popup is
  // same-origin so opener access is fine.
  const w = window.open('', '_blank');
  if (!w || w.closed) {
    throw new Error('Popup blocked — allow popups for this site to export.');
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  const trigger = (): void => { try { w.focus(); w.print(); } catch { /* ignore */ } };
  // Wait for the new doc to finish parsing before printing. Some browsers
  // fire load synchronously after document.close(); fall back to a timer.
  if (w.document.readyState === 'complete') trigger();
  else w.addEventListener('load', trigger, { once: true });
  window.setTimeout(trigger, 600);
}

// ── Pure HTML builders ──────────────────────────────────────────────────
// Exported separately from the print-popup wrappers so they can be unit
// tested without a window. Each returns a complete HTML document string.

export function buildSpecsHtml(p: ProjectRow, rooms: RoomRow[], specs: SpecRow[]): string {
  const roomNameById = new Map(rooms.map((r) => [r.id, r.name] as const));
  const rows = specs.map((s) => `
    <tr>
      <td><strong>${esc(s.item)}</strong>${s.sku ? `<br><span style="color:#8a837a;font-size:10px">SKU ${esc(s.sku)}</span>` : ''}</td>
      <td>${esc(roomNameById.get(s.room_id) ?? '—')}</td>
      <td>${esc(s.vendor ?? '—')}</td>
      <td>${s.quantity}${s.unit ? ` ${esc(s.unit)}` : ''}</td>
      <td>${fmtMoney(s.unit_cost_cents == null ? null : s.unit_cost_cents * s.quantity)}</td>
      <td>${esc(fmtDate(s.estimated_delivery))}</td>
      <td>${esc(SPEC_STATUS_LABEL[s.status])}</td>
    </tr>`).join('');
  const body = specs.length === 0
    ? `<p style="color:#8a837a">No specs in this project yet.</p>`
    : `<table><thead><tr><th>Item</th><th>Room</th><th>Vendor</th><th>Qty</th><th>Ext. Cost</th><th>Delivery</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`;
  return PRINT_HEAD('Materials Sheet') + header(p, 'Materials Sheet') + body + PRINT_FOOT;
}

export function buildContactsHtml(p: ProjectRow, contacts: DesignerContactRow[]): string {
  const rows = contacts.map((c) => `
    <tr>
      <td><strong>${esc(c.name)}</strong></td>
      <td>${esc(c.role ?? '—')}</td>
      <td>${esc(c.company ?? '—')}</td>
      <td>${esc(c.phone ?? '—')}</td>
      <td>${esc(c.email ?? '—')}</td>
    </tr>`).join('');
  const body = contacts.length === 0
    ? `<p style="color:#8a837a">No contacts on this project.</p>`
    : `<table><thead><tr><th>Name</th><th>Role</th><th>Company</th><th>Phone</th><th>Email</th></tr></thead><tbody>${rows}</tbody></table>`;
  return PRINT_HEAD('Project Contacts') + header(p, 'Project Contacts') + body + PRINT_FOOT;
}

export function buildPunchHtml(
  p: ProjectRow,
  rooms: RoomRow[],
  punch: PunchItemRow[],
  // Required, not optional-with-a-default: since 0040 a punch item's vendor is
  // a contact id, and a caller that forgot to pass these would print a list
  // with every vendor blank and nothing on the page to say why.
  contacts: DesignerContactRow[],
): string {
  const sections = rooms.map((r) => {
    const items = punch.filter((x) => x.room_id === r.id);
    if (items.length === 0) return '';
    const rows = items.map((x) => {
      const vendor = punchVendorName(x, contacts);
      return `
      <tr>
        <td style="width:18px">${x.complete ? '☑' : '☐'}</td>
        <td>${esc(x.description)}${vendor ? `<br><span style="color:#8a837a;font-size:10px">${esc(vendor)}</span>` : ''}</td>
        <td style="width:90px;color:#8a837a">${esc(fmtDate(x.due_date))}</td>
      </tr>`;
    }).join('');
    return `<h2>${esc(r.name)}</h2><table><tbody>${rows}</tbody></table>`;
  }).join('');
  const body = sections || `<p style="color:#8a837a">No punch list items have been added.</p>`;
  return PRINT_HEAD('Punch List') + header(p, 'Punch List') + body + PRINT_FOOT;
}

export interface PresentationImage {
  room_id: string;
  kind: 'before' | 'after' | 'inspiration' | 'detail' | 'in_progress';
  url: string;
  caption: string | null;
  sort_order: number;
}

export function buildPresentationHtml(
  p: ProjectRow,
  rooms: RoomRow[],
  specs: SpecRow[],
  palette: PaletteColorRow[],
  questionnaire: QuestionnaireRow | null,
  roomImages: PresentationImage[] = [],
): string {
  const overview = `
    <h2>Project overview</h2>
    <table><tbody>
      <tr><td style="width:140px"><strong>Client</strong></td><td>${esc(p.client_name ?? '—')}</td></tr>
      ${p.address ? `<tr><td><strong>Address</strong></td><td>${esc(p.address)}</td></tr>` : ''}
      <tr><td><strong>Timeline</strong></td><td>${esc(fmtDate(p.start_date))} → ${esc(fmtDate(p.target_completion_date))}</td></tr>
      <tr><td><strong>Budget</strong></td><td>${esc(fmtMoney(p.budget_cents))}</td></tr>
      ${p.description ? `<tr><td><strong>Brief</strong></td><td>${esc(p.description)}</td></tr>` : ''}
    </tbody></table>`;

  const paletteBlock = palette.length === 0 ? '' : `
    <h2>Palette</h2>
    <div class="swatch-row">
      ${palette.map((c) => `
        <div class="sw">
          <div class="chip" style="background:${esc(c.hex)}"></div>
          <div class="name">${esc(c.name)}</div>
          <div class="meta">${esc(c.hex)}${c.code ? ` · ${esc(c.code)}` : ''}</div>
        </div>`).join('')}
    </div>`;

  const roomList = rooms.length === 0 ? '' : `
    <h2>Interior &amp; exterior</h2>
    <table><thead><tr><th>Room</th><th>Location</th><th>Status</th></tr></thead><tbody>
      ${rooms.map((r) => `<tr><td><strong>${esc(r.name)}</strong></td><td>${r.is_exterior ? 'Exterior' : 'Interior'}</td><td>${esc(ROOM_STATUS_LABEL[r.status])}</td></tr>`).join('')}
    </tbody></table>`;

  const specsByRoom = rooms.map((r) => {
    const rs = specs.filter((s) => s.room_id === r.id);
    if (rs.length === 0) return '';
    return `<h2 class="page-break">${esc(r.name)}</h2>
      <table><thead><tr><th>Item</th><th>Vendor</th><th>Qty</th><th>Ext. Cost</th><th>Status</th></tr></thead><tbody>
        ${rs.map((s) => `<tr><td>${esc(s.item)}</td><td>${esc(s.vendor ?? '—')}</td><td>${s.quantity}${s.unit ? ` ${esc(s.unit)}` : ''}</td><td>${esc(fmtMoney(s.unit_cost_cents == null ? null : s.unit_cost_cents * s.quantity))}</td><td>${esc(SPEC_STATUS_LABEL[s.status])}</td></tr>`).join('')}
      </tbody></table>`;
  }).join('');

  const beforeAfterByRoom = roomImages.length === 0 ? '' : rooms.map((r) => {
    const before = roomImages.filter((i) => i.room_id === r.id && i.kind === 'before').sort((a, b) => a.sort_order - b.sort_order);
    const after = roomImages.filter((i) => i.room_id === r.id && i.kind === 'after').sort((a, b) => a.sort_order - b.sort_order);
    const pairs = Math.min(before.length, after.length);
    if (pairs === 0) return '';
    const rows = Array.from({ length: pairs }, (_, i) => `
      <tr>
        <td style="width:50%;text-align:center"><img src="${esc(before[i]!.url)}" style="max-width:100%;max-height:280px"/><div style="font-size:10px;color:#8a837a;margin-top:4px">Before${before[i]!.caption ? ` — ${esc(before[i]!.caption!)}` : ''}</div></td>
        <td style="width:50%;text-align:center"><img src="${esc(after[i]!.url)}" style="max-width:100%;max-height:280px"/><div style="font-size:10px;color:#8a837a;margin-top:4px">After${after[i]!.caption ? ` — ${esc(after[i]!.caption!)}` : ''}</div></td>
      </tr>`).join('');
    return `<h2 class="page-break">${esc(r.name)} — Before &amp; After</h2><table><tbody>${rows}</tbody></table>`;
  }).join('');

  const responses = readResponses(questionnaire);
  const questionnaireBlock = !questionnaire ? '' : `
    <h2 class="page-break">Style questionnaire</h2>
    ${QUESTIONNAIRE_FIELDS.map((f) => {
      const a = (responses[f.key] ?? '').trim();
      return `<div class="q-block">
        <div class="q">${esc(f.label)}</div>
        <div class="a ${a ? '' : 'empty'}">${a ? esc(a) : '—'}</div>
      </div>`;
    }).join('')}`;

  return PRINT_HEAD('Client Presentation')
    + header(p, p.name)
    + overview
    + paletteBlock
    + roomList
    + specsByRoom
    + beforeAfterByRoom
    + questionnaireBlock
    + PRINT_FOOT;
}

// ── Print-popup wrappers (the existing public API) ──────────────────────

export function exportSpecs(p: ProjectRow, rooms: RoomRow[], specs: SpecRow[]): void {
  openPrint(buildSpecsHtml(p, rooms, specs));
}
export function exportContacts(p: ProjectRow, contacts: DesignerContactRow[]): void {
  openPrint(buildContactsHtml(p, contacts));
}
export function exportPunch(p: ProjectRow, rooms: RoomRow[], punch: PunchItemRow[], contacts: DesignerContactRow[]): void {
  openPrint(buildPunchHtml(p, rooms, punch, contacts));
}
export function exportPresentation(
  p: ProjectRow,
  rooms: RoomRow[],
  specs: SpecRow[],
  palette: PaletteColorRow[],
  questionnaire: QuestionnaireRow | null,
  roomImages: PresentationImage[] = [],
): void {
  openPrint(buildPresentationHtml(p, rooms, specs, palette, questionnaire, roomImages));
}
